/**
 * Two panels that used to be one section, split because they are two different
 * kinds of evidence and the weaker one was wearing the stronger one's clothes.
 *
 * - **`ThrottlingPanel`** — batch throttling. A real F&O platform signal: jobs
 *   queued behind the throttle, which nothing outside the tenant can observe.
 *   It sits with the evidence on the board, not down here.
 * - **`AvailabilityControlSection`** — the synthetic probe, demoted to what it
 *   is: a **control**. It is the last section on the board and it is framed as
 *   *what the front door answered, from outside*.
 *
 * ## Why the probe is a control and not the headline
 *
 * `NAX365` is a URL-ping web test. It follows redirects to a 200 — in practice
 * from `login.microsoftonline.com` — matches no content, never signs in, and
 * retries enough to swallow most transient failures. Measured over seven days
 * it ran 26,230 times and failed zero times, at a West Europe p50 of 203 ms,
 * while an hour in the same window had users waiting on a form p95 of 8,797 ms.
 *
 * So a green result here rules out the network path and the front door, and
 * says nothing whatever about the application tier, the database, sign-in,
 * maintenance mode or how long a form took. That sentence is rendered beside
 * the numbers rather than only in this comment, because a reader who takes this
 * section for "F&O was healthy" has been misled by the panel.
 *
 * A control still earns its place: paired against an hour of real pain it
 * settles "was it them or was it us" without opening a second tool. That
 * pairing — felt p95 against probe p50 on one shared axis — is the pinpoint
 * board's `PlatformSection`, and this section links to it rather than drawing a
 * second copy.
 *
 * ## The chart choices, which are rules rather than preferences
 *
 * 1. **A gap is a gap.** `FoAvailabilityPoint` is absent for a 5-minute bin in
 *    which no probe ran, and a bin whose `availability_pct` is null is dropped
 *    here rather than coerced. Recharts leaves the line broken (`connectNulls`
 *    defaults off), so a quiet window reads as "not measured" and never as a
 *    dip to zero. Interpolating across it would manufacture an outage — or,
 *    worse, manufacture uptime.
 *
 * 2. **The magnitude chart plots failures, not availability.** Availability
 *    lives at 99-point-something, so a zero-based bar of it is a row of
 *    identical full bars, and a bar axis that does *not* start at zero is a
 *    lie. Failed probe executions are a true zero-based magnitude, so that is
 *    what the bar compares; the percentage stays a `Meter` in the drawer, where
 *    it can carry its threshold honestly.
 *
 * 3. **An environment that ran no probes is excluded from the bar.** It would
 *    otherwise plot zero failures and rank as the healthiest thing on the
 *    board. Those environments are named separately, as `unknown`.
 *
 * 4. **The control never wears a success tone.** A probe that answered is the
 *    absence of one finding, not the presence of a good one, so the strip goes
 *    danger-toned when it breaches the threshold and stays neutral otherwise.
 *    Green here is precisely the misreading the section exists to prevent.
 *
 * ## The one drawer, and who renders it
 *
 * Both panels drill into the same URL-addressable environment drawer
 * (`?env=`). Exactly one component may render it or the reader gets two
 * overlays for one param, so `AvailabilityControlSection` owns it — it is the
 * panel that ranks environments — and `ThrottlingPanel` only writes the param.
 * `features/dynamics/index.tsx` renders both under the same `isPanelOk` gate,
 * which is what keeps that arrangement true.
 */

import { Chart } from "@norican/dataviz";
import { Globe2, Icon } from "@norican/icons";
import {
  Badge,
  Card,
  DataGrid,
  EmptyState,
  Freshness,
  Meter,
  StatisticBar,
  type StatisticBarItem,
  Typography,
} from "@norican/react";

import { BoardSection, ChartCard, DetailDrawer } from "@/components/board";
import {
  type FoAvailabilityPoint,
  type FoEnvironmentStatus,
  type FoOverview,
  useFoInvestigatorAccess,
} from "@/lib/api";
import { identityConfig } from "@/lib/charts/config";
import { useDrawerParam } from "@/lib/drill";

import { BoardCrossLink } from "./cross-link";
import { PanelCard } from "./panel-card";
import { AVAILABILITY_ANCHOR, DYNAMICS_PINPOINT_PATH, ENV_DRAWER_PARAM } from "./params";
import {
  formatBinTime,
  formatCount,
  formatMs,
  formatPercent,
  formatWindow,
  HEALTH_COLOR,
  HEALTH_ICON,
  HEALTH_LABEL,
  HEALTH_ORDER,
  HEALTH_TONE,
  tooltipValue,
} from "./status";

/**
 * In-page anchor of the batch-throttling panel.
 *
 * Deliberately not in `./params.ts` with the others: that module is the board's
 * shared URL vocabulary and every anchor in it is read by at least two files,
 * whereas this one is written by the panel below and read by the KPI tile that
 * opens it. It lives with the section it names until a third reader appears.
 */
export const THROTTLING_ANCHOR = "fo-throttling";

/**
 * What the synthetic test does and does not prove, in one sentence, rendered
 * wherever its numbers are.
 *
 * It mirrors `PROBE_CAVEAT` in `backend/src/nova/fo_console/service.py`, which
 * reaches the frontend as `FoConsolePlatform.probe_caveat` — but on the
 * `/fo/console/platform` payload, which this board does not read. `/fo/overview`
 * carries no caveat field, and adding one is an API change this work is not
 * making, so the sentence is duplicated here and the duplication is flagged.
 */
const PROBE_CAVEAT =
  "A URL ping that follows redirects and never signs in. A healthy result rules out the " +
  "network path and the front door for the minutes it ran; it says nothing about the " +
  "application tier, the database, sign-in, maintenance mode, or how long a form took.";

/** A trend row: one 5-minute bin, one numeric column per environment present. */
type TrendRow = { bucket: string; label: string } & Record<string, number | string>;

/** How many throttled environments the L1 panel ranks; the rest are named, not sliced away. */
const THROTTLING_LIMIT = 5;

interface ThrottlingPanelProps {
  overview: FoOverview;
}

/**
 * Batch throttling (L1): which environments queued batch work behind the
 * throttle, worst first.
 *
 * Three states, and they are three different findings (§5 — unconfigured ≠
 * empty ≠ zero):
 *
 * - `null` — the throttling query failed while the rest of the overview
 *   answered, so the count is unknown. Falling through to "no panel" would have
 *   rendered that failure as a quiet, throttle-free window.
 * - `0` — the query answered and counted none. A measured zero, and it says so,
 *   because an anchor that lands on nothing is a broken door.
 * - `> 0` — the worst five environments, each row opening its drawer.
 */
export function ThrottlingPanel({ overview }: ThrottlingPanelProps) {
  // Writes `?env=`; the drawer itself is rendered once, by the control section.
  const drawer = useDrawerParam(ENV_DRAWER_PARAM);
  const windowLabel = formatWindow(overview.window);
  const events = overview.summary.throttling_events;
  const warnAbove = overview.summary.throttling_warning_events;

  const throttled = [...overview.environments]
    .filter((row) => row.throttling_events > 0)
    .sort(
      (a, b) =>
        b.throttling_events - a.throttling_events || a.environment.localeCompare(b.environment),
    );
  const ranked = throttled.slice(0, THROTTLING_LIMIT);
  const unranked = throttled.slice(THROTTLING_LIMIT);

  const caption =
    events == null
      ? "Not measured in this window"
      : events === 0
        ? `${windowLabel} · warns above ${formatCount(warnAbove)}`
        : `${formatCount(events)} events in the ${windowLabel} · warns above ${formatCount(warnAbove)}`;

  return (
    <section id={THROTTLING_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      <PanelCard title="Batch throttling" caption={caption}>
        {events == null && (
          <EmptyState
            size="sm"
            title="Batch throttling could not be read"
            description="The throttling query failed while the availability panels answered, so the event count is unknown rather than zero. The cause is listed in the board's errors."
          />
        )}

        {events === 0 && (
          <EmptyState
            size="sm"
            title="No batch job was throttled in this window"
            description="The throttling query answered and counted none. This is a measured zero, not a missing measurement — and it is a signal the synthetic probe below could never have produced."
          />
        )}

        {events != null && events > 0 && (
          <div className="flex flex-col gap-3">
            <DataGrid<FoEnvironmentStatus>
              data={ranked}
              getRowId={(row) => row.environment}
              density="compact"
              aria-label="Environments with batch throttling, worst first"
              columns={[
                {
                  id: "environment",
                  header: "Environment",
                  accessor: (row) => row.environment,
                  cell: (row) => (
                    <button
                      type="button"
                      className="text-fg text-left font-medium hover:underline"
                      onClick={() => drawer.open(row.environment)}
                    >
                      {row.environment}
                    </button>
                  ),
                },
                {
                  id: "events",
                  header: "Throttling events",
                  align: "end",
                  accessor: (row) => row.throttling_events,
                  cell: (row) => (
                    <span className="tabular-nums">{formatCount(row.throttling_events)}</span>
                  ),
                },
                {
                  id: "classes",
                  header: "Batch classes",
                  align: "end",
                  accessor: (row) => row.distinct_batch_classes,
                  cell: (row) => (
                    <span className="tabular-nums">{formatCount(row.distinct_batch_classes)}</span>
                  ),
                },
              ]}
            />
            {unranked.length > 0 && (
              <Typography variant="body-sm" tone="muted">
                {formatCount(unranked.length)} further{" "}
                {unranked.length === 1 ? "environment" : "environments"} were throttled in this
                window and are not ranked above: {unranked.map((row) => row.environment).join(", ")}
                . Every one of them, with its event count, is in the environment table at the foot
                of the board.
              </Typography>
            )}
          </div>
        )}
      </PanelCard>
    </section>
  );
}

interface AvailabilityControlSectionProps {
  overview: FoOverview;
}

/**
 * The board's control section (L1, and deliberately last): the synthetic-probe
 * trend, failures per environment, and each environment's full row one click
 * deeper in a URL-addressable drawer (L2).
 *
 * Every number the KPI strip used to lead with is here — availability, probe
 * runs, failed runs, the environments they were measured across — under a frame
 * that says what they can and cannot settle.
 */
export function AvailabilityControlSection({ overview }: AvailabilityControlSectionProps) {
  const drawer = useDrawerParam(ENV_DRAWER_PARAM);
  const windowLabel = formatWindow(overview.window);
  const staleAfterMs = overview.cache_ttl_sec * 2000;
  const summary = overview.summary;
  // The hour-by-hour pairing of felt pain against probe latency lives on the
  // pinpoint board, which is investigator-gated. Offering the door to a reader
  // the backend would refuse is offering a 403, so the gate decides.
  const canOpenPinpoint = useFoInvestigatorAccess().access !== "denied";

  // Colour assignment comes from the FULL environment list, sorted by name, so
  // an environment keeps its colour whatever the trend happens to contain in
  // this window (§3.3 — a filter must not repaint the survivors).
  const environmentNames = [...new Set(overview.environments.map((row) => row.environment))].sort(
    (a, b) => a.localeCompare(b),
  );
  const trendConfig = identityConfig(environmentNames);

  // Pivot the flat trend into one row per bin. A point with a null percentage
  // is skipped, leaving the key absent — which is what draws the gap.
  const byBucket = new Map<string, Record<string, number>>();
  for (const point of overview.availability_trend) {
    if (point.availability_pct == null) {
      continue;
    }
    const row = byBucket.get(point.bucket) ?? {};
    row[point.environment] = point.availability_pct;
    byBucket.set(point.bucket, row);
  }
  const trendRows: TrendRow[] = [...byBucket.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucket, series]) => ({ bucket, label: formatBinTime(bucket), ...series }));

  // Only environments that actually ran probes can be ranked by failures.
  const measured = overview.environments.filter((row) => row.total_tests > 0);
  const unmeasured = overview.environments.filter((row) => row.total_tests === 0);
  const failureRows = [...measured].sort(
    (a, b) => b.failed_tests - a.failed_tests || a.environment.localeCompare(b.environment),
  );

  const verdictCounts = HEALTH_ORDER.map((state) => ({
    state,
    count: overview.environments.filter((row) => row.state === state).length,
  })).filter((entry) => entry.count > 0);

  const openEnvironment = drawer.value
    ? overview.environments.find((row) => row.environment === drawer.value)
    : undefined;

  /* The control strip. A breach is danger-toned; everything else is neutral —
     never `positive`, because a probe that answered is the absence of one
     finding, not evidence of health (§4 in the file header). */
  const controlItems: StatisticBarItem[] = [
    {
      label: "Front door answered",
      value: formatPercent(summary.availability_pct),
      tone:
        summary.availability_pct != null &&
        summary.availability_pct < summary.availability_warning_pct
          ? "negative"
          : "neutral",
      caption:
        summary.availability_pct == null
          ? "No synthetic test ran — unknown is not 100%"
          : `warns below ${formatPercent(summary.availability_warning_pct)}`,
    },
    {
      label: "Probe runs",
      value: formatCount(summary.synthetic_tests_total),
      tone: "neutral",
      // Coverage beside the percentage (§5): a 100% computed from one
      // environment is a much weaker 100% than one computed from all of them.
      caption:
        summary.synthetic_tests_total == null || summary.environments_reporting == null
          ? "Coverage unknown — a probe query failed"
          : `across ${formatCount(
              summary.environments_reporting,
            )} environments in the ${windowLabel}`,
    },
    {
      label: "Failed runs",
      value: formatCount(summary.synthetic_tests_failed),
      tone:
        summary.synthetic_tests_failed != null && summary.synthetic_tests_failed > 0
          ? "negative"
          : "neutral",
      caption:
        summary.synthetic_tests_failed == null
          ? "Probe results could not be read — unknown, not clean"
          : summary.synthetic_tests_total == null
            ? "The run count could not be read, so this is a failure count with no base"
            : summary.synthetic_tests_total === 0
              ? "No probe executions in the window"
              : "the web test retries, so most transient failures never reach this count",
    },
  ];

  return (
    <BoardSection
      id={AVAILABILITY_ANCHOR}
      eyebrow="Control"
      title="What the front door answered, from outside"
      lead="Microsoft's synthetic tests hit the F&O URL every few minutes from Azure locations outside Norican. They are the control on the evidence above, not a verdict on it."
      bottomLine="Use this to rule a cause out, never to rule the estate healthy: a green probe eliminates the network path and the front door for the minutes it ran, and every other cause of a slow morning survives it untouched."
    >
      <Card variant="subtle">
        <Card.Body>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <StatisticBar
                items={controlItems}
                size="md"
                aria-label={`Synthetic probe control figures, ${windowLabel}`}
              />
              {overview.collected_at && (
                <Freshness
                  label="Log Analytics read"
                  since={overview.collected_at}
                  staleAfter={staleAfterMs}
                />
              )}
            </div>
            {/* The caveat sits with the numbers, not only in the section lead —
                this is the sentence that stops the strip being read as health. */}
            <Typography variant="body-sm" tone="muted">
              {PROBE_CAVEAT}
            </Typography>
          </div>
        </Card.Body>
      </Card>

      <ChartCard<FoAvailabilityPoint>
        title="Synthetic availability"
        caption={`5-minute bins, ${windowLabel} · URL ping, no sign-in · a bin with no probe run is a gap, not a zero`}
        since={overview.collected_at}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: "fo-availability-trend",
          ariaLabel: "Synthetic availability per 5-minute bin and environment",
          data: overview.availability_trend,
          getRowId: (row) => `${row.bucket}-${row.environment}`,
          columns: [
            { id: "bucket", header: "Bin", accessor: (row) => formatBinTime(row.bucket) },
            { id: "environment", header: "Environment", accessor: (row) => row.environment },
            {
              id: "availability",
              header: "Availability",
              align: "end",
              accessor: (row) => row.availability_pct,
              cell: (row) => (
                <span className="tabular-nums">{formatPercent(row.availability_pct)}</span>
              ),
            },
            {
              id: "tests",
              header: "Runs",
              align: "end",
              accessor: (row) => row.total_tests,
              cell: (row) => <span className="tabular-nums">{formatCount(row.total_tests)}</span>,
            },
            {
              id: "failed",
              header: "Failed",
              align: "end",
              accessor: (row) => row.failed_tests,
              cell: (row) => <span className="tabular-nums">{formatCount(row.failed_tests)}</span>,
            },
          ],
        }}
      >
        {trendRows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No synthetic probe results in this window"
            description="The workspace answered, and it held no probe executions to report. That is an absence of tests, not a run of failures."
          />
        ) : (
          <Chart.Container
            config={trendConfig}
            className="h-72"
            aria-label={`Synthetic availability per environment in 5-minute bins, ${windowLabel}`}
          >
            <Chart.LineChart data={trendRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" />
              <Chart.YAxis
                {...Chart.axisProps}
                width={44}
                domain={["auto", 100] as const}
                tickFormatter={(value: number) => `${value}%`}
              />
              <Chart.Tooltip
                content={
                  // The trend is pivoted per bucket, so an environment that did
                  // not probe this bin has no key at all — `undefined%` was the
                  // rendering before, which reads as a real reading. §5.
                  <Chart.TooltipContent formatter={(value) => formatPercent(tooltipValue(value))} />
                }
              />
              <Chart.ReferenceLine
                y={summary.availability_warning_pct}
                stroke="var(--norican-warning)"
                strokeDasharray="4 4"
                label={{
                  value: `warns below ${formatPercent(summary.availability_warning_pct)}`,
                  position: "insideBottomRight",
                  fontSize: 11,
                  fill: "var(--norican-fg-muted)",
                }}
              />
              {environmentNames.map((environment) => (
                <Chart.Line
                  key={environment}
                  {...Chart.seriesDefaults}
                  dataKey={environment}
                  type="monotone"
                  stroke={trendConfig[environment]?.color}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
              {/* Legend only from two series up (§6); with one, the title names it. */}
              {environmentNames.length >= 2 && <Chart.Legend content={<Chart.LegendContent />} />}
            </Chart.LineChart>
          </Chart.Container>
        )}
      </ChartCard>

      <ChartCard<FoEnvironmentStatus>
        title="Failed synthetic tests by environment"
        caption={`${formatCount(measured.length)} of ${formatCount(
          overview.environments.length,
        )} environments ran probes · URL ping, no sign-in · click a bar for its full row`}
        since={overview.collected_at}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: "fo-environments",
          ariaLabel: "Every reporting F&O environment and its verdict",
          data: overview.environments,
          getRowId: (row) => row.environment,
          columns: [
            { id: "environment", header: "Environment", accessor: (row) => row.environment },
            {
              id: "state",
              header: "Verdict",
              accessor: (row) => row.state,
              cell: (row) => (
                <Badge tone={HEALTH_TONE[row.state]} variant="subtle" size="xs">
                  {HEALTH_LABEL[row.state]}
                </Badge>
              ),
            },
            {
              id: "availability",
              header: "Availability",
              align: "end",
              accessor: (row) => row.availability_pct,
              cell: (row) => (
                <span className="tabular-nums">{formatPercent(row.availability_pct)}</span>
              ),
            },
            {
              id: "tests",
              header: "Runs",
              align: "end",
              accessor: (row) => row.total_tests,
              cell: (row) => <span className="tabular-nums">{formatCount(row.total_tests)}</span>,
            },
            {
              id: "failed",
              header: "Failed",
              align: "end",
              accessor: (row) => row.failed_tests,
              cell: (row) => <span className="tabular-nums">{formatCount(row.failed_tests)}</span>,
            },
            {
              id: "throttling",
              header: "Throttling",
              align: "end",
              accessor: (row) => row.throttling_events,
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.throttling_events)}</span>
              ),
            },
            {
              id: "duration",
              header: "Avg probe",
              align: "end",
              accessor: (row) => row.avg_duration_ms,
              cell: (row) => <span className="tabular-nums">{formatMs(row.avg_duration_ms)}</span>,
            },
          ],
        }}
      >
        <div className="flex flex-col gap-3">
          {failureRows.length === 0 ? (
            <EmptyState
              size="sm"
              title="No environment ran a synthetic probe in this window"
              description="Nothing was measured, so nothing is ranked. This is not a clean bill of health."
            />
          ) : (
            <Chart.Container
              config={{ failed_tests: { label: "Failed probe runs" } }}
              className="h-64"
              aria-label={`Failed synthetic test executions per environment, ${windowLabel}, worst first`}
            >
              <Chart.BarChart
                data={failureRows}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
                <Chart.YAxis
                  {...Chart.axisProps}
                  type="category"
                  dataKey="environment"
                  width={160}
                />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: FoEnvironmentStatus) => [
                        { label: "Verdict", value: HEALTH_LABEL[datum.state] },
                        { label: "Availability", value: formatPercent(datum.availability_pct) },
                        {
                          label: "Failed / runs",
                          value: `${formatCount(datum.failed_tests)} / ${formatCount(
                            datum.total_tests,
                          )}`,
                        },
                        { label: "Throttling events", value: formatCount(datum.throttling_events) },
                      ]}
                    />
                  }
                />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="failed_tests"
                  radius={[0, 3, 3, 0]}
                  cursor="pointer"
                  onClick={(_, index) => {
                    const row = failureRows[index];
                    if (row) {
                      drawer.open(row.environment);
                    }
                  }}
                >
                  {failureRows.map((row) => (
                    <Chart.Cell key={row.environment} fill={HEALTH_COLOR[row.state]} />
                  ))}
                </Chart.Bar>
              </Chart.BarChart>
            </Chart.Container>
          )}

          {/* Verdict legend with icons — a bar's colour never carries the status
              on its own (§3.2). */}
          {verdictCounts.length > 0 && (
            <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
              {verdictCounts.map(({ state, count }) => (
                <span key={state} className="inline-flex items-center gap-1.5">
                  <Icon icon={HEALTH_ICON[state]} size="xs" />
                  {HEALTH_LABEL[state]} ({count})
                </span>
              ))}
            </div>
          )}

          {unmeasured.length > 0 && (
            <Typography variant="body-sm" tone="muted">
              Not ranked above, because zero failures out of zero runs is not a pass:{" "}
              {unmeasured.map((row) => row.environment).join(", ")} produced no probe executions in
              the {windowLabel}.
            </Typography>
          )}
        </div>
      </ChartCard>

      {canOpenPinpoint && (
        <BoardCrossLink
          title="Was it them, or was it us — hour by hour"
          caption="A control is worth most beside the pain it qualifies. The pinpoint board pairs each hour's felt form P95 against the probe's response on one shared millisecond axis, so a 40× gap reads as a gap instead of two numbers in adjacent cells."
          to={DYNAMICS_PINPOINT_PATH}
          cta="Pinpoint →"
          icon={Globe2}
        />
      )}

      {openEnvironment && (
        <DetailDrawer
          open
          onClose={drawer.close}
          title={openEnvironment.environment}
          breadcrumbs={[{ label: "Dynamics", to: "/dynamics" }, { label: "Environments" }]}
          items={[
            { label: "Verdict", value: HEALTH_LABEL[openEnvironment.state] },
            { label: "Availability", value: formatPercent(openEnvironment.availability_pct) },
            { label: "Probe executions", value: formatCount(openEnvironment.total_tests) },
            { label: "Failed executions", value: formatCount(openEnvironment.failed_tests) },
            { label: "Distinct probes", value: formatCount(openEnvironment.distinct_tests) },
            { label: "Mean probe duration", value: formatMs(openEnvironment.avg_duration_ms) },
            { label: "Throttling events", value: formatCount(openEnvironment.throttling_events) },
            {
              label: "Throttled batch classes",
              value: formatCount(openEnvironment.distinct_batch_classes),
            },
            { label: "Window", value: formatWindow(overview.window) },
            { label: "Workspace", value: overview.workspace },
          ]}
        >
          {openEnvironment.availability_pct == null ? (
            <Typography variant="body-sm" tone="muted">
              No probe ran against this environment in the {windowLabel}, so it has no availability
              figure. Its verdict is <strong className="text-fg">unknown</strong>, which is not the
              same as healthy.
            </Typography>
          ) : (
            <Meter.Root
              value={openEnvironment.availability_pct}
              min={0}
              max={100}
              size="md"
              // `accent` above the threshold, never `success` — this was the
              // last place in the section that painted the probe green by name.
              // (`MeterTone` has no neutral; accent is its non-status default.)
              // Below the threshold it stays danger: a probe that did NOT answer
              // is a real finding, and the only one this signal can make alone.
              tone={
                openEnvironment.availability_pct < summary.availability_warning_pct
                  ? "danger"
                  : "accent"
              }
              aria-valuetext={`${formatPercent(openEnvironment.availability_pct)} of probe runs succeeded`}
            >
              <div className="mb-1.5 flex justify-between">
                <Meter.Label>
                  Availability against the {formatPercent(summary.availability_warning_pct)}{" "}
                  threshold
                </Meter.Label>
                <Meter.Value>{() => formatPercent(openEnvironment.availability_pct)}</Meter.Value>
              </div>
              <Meter.Track>
                <Meter.Indicator />
              </Meter.Track>
            </Meter.Root>
          )}
        </DetailDrawer>
      )}
    </BoardSection>
  );
}
